import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Copy, KeyRound, Loader2, X } from 'lucide-react';
import { Button } from '@lms/ui';
import { Bilingual } from './Bilingual';
import { copy } from '../copy/copy';
import { useLanguage } from '../lib/LanguageContext';

type DialogState = 'idle' | 'loading' | 'success';

interface ParticipantTemporaryPasswordDialogProps {
  participantName: string;
  onClose: () => void;
  onSave: (password: string) => Promise<void>;
}

export function ParticipantTemporaryPasswordDialog({
  participantName,
  onClose,
  onSave,
}: ParticipantTemporaryPasswordDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [state, setState] = useState<DialogState>('idle');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useLanguage();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  const handleClose = () => {
    if (state !== 'loading') onClose();
  };

  const handleSubmit = async () => {
    if (password.length < 8) {
      setErrorMessage(t(copy.users.passwordRecovery.minimumLength));
      return;
    }
    if (password !== confirmation) {
      setErrorMessage(t(copy.users.passwordRecovery.confirmationMismatch));
      return;
    }

    setState('loading');
    setErrorMessage('');
    try {
      await onSave(password);
      setState('success');
    } catch (error) {
      setState('idle');
      setErrorMessage(
        error instanceof Error ? error.message : t(copy.users.passwordRecovery.errorMessage)
      );
    }
  };

  const copyPassword = async () => {
    await navigator.clipboard.writeText(password);
  };

  return (
    <dialog
      ref={dialogRef}
      className="m-auto w-[calc(100%-2rem)] max-w-lg rounded-[1.75rem] bg-surface-container-lowest p-0 text-on-surface shadow-level5 backdrop:bg-on-surface/45 backdrop:backdrop-blur-sm"
      aria-labelledby="participant-temporary-password-title"
      onCancel={event => {
        event.preventDefault();
        handleClose();
      }}
      onClick={event => {
        if (event.target === event.currentTarget) handleClose();
      }}
    >
      <div className="space-y-6 p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="grid size-11 shrink-0 place-items-center rounded-2xl bg-secondary-container text-on-secondary-container">
              <KeyRound size={21} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
                <Bilingual {...copy.users.passwordRecovery.eyebrow} />
              </p>
              <h2
                id="participant-temporary-password-title"
                className="mt-1 font-headline text-xl font-bold tracking-tight text-on-surface"
              >
                <Bilingual {...copy.users.passwordRecovery.title} />
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={state === 'loading'}
            aria-label={t(copy.users.passwordRecovery.closeLabel)}
            className="grid size-11 shrink-0 place-items-center rounded-xl text-on-surface-variant transition-colors duration-150 hover:bg-surface-container-high hover:text-on-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50 active:scale-95"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {state === 'success' ? (
          <div
            role="status"
            className="rounded-2xl bg-success-container p-5 text-on-success-container"
          >
            <CheckCircle2 size={22} aria-hidden="true" />
            <p className="mt-3 text-sm font-label font-bold">
              <Bilingual {...copy.users.passwordRecovery.successTitle} />
            </p>
            <p className="mt-1 text-xs leading-relaxed">
              {t(copy.users.passwordRecovery.successDescription).replace('{name}', participantName)}
            </p>
            <div className="mt-4 flex items-center gap-2 rounded-xl bg-on-success-container/10 p-3">
              <code className="min-w-0 flex-1 truncate text-sm font-semibold">{password}</code>
              <Button
                type="button"
                variant="ghost"
                onClick={() => void copyPassword()}
                className="h-9 shrink-0 rounded-lg px-3 text-xs font-label font-bold text-on-success-container hover:bg-on-success-container/10"
              >
                <Copy size={14} />
                <Bilingual {...copy.users.passwordRecovery.copyAction} />
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="m-0 text-sm leading-relaxed text-on-surface-variant">
              <Bilingual {...copy.users.passwordRecovery.description} />
            </p>
            <div className="space-y-4 rounded-2xl bg-surface-container-low p-4 sm:p-5">
              <label className="block text-xs font-label font-bold text-on-surface">
                <Bilingual {...copy.users.passwordRecovery.passwordLabel} />
                <input
                  type="password"
                  value={password}
                  onChange={event => setPassword(event.target.value)}
                  autoComplete="new-password"
                  className="mt-2 h-11 w-full rounded-xl bg-surface-container-lowest px-3 text-sm text-on-surface outline-none transition-shadow placeholder:text-on-surface-variant/70 focus-visible:ring-2 focus-visible:ring-primary/20"
                />
              </label>
              <label className="block text-xs font-label font-bold text-on-surface">
                <Bilingual {...copy.users.passwordRecovery.confirmationLabel} />
                <input
                  type="password"
                  value={confirmation}
                  onChange={event => setConfirmation(event.target.value)}
                  autoComplete="new-password"
                  className="mt-2 h-11 w-full rounded-xl bg-surface-container-lowest px-3 text-sm text-on-surface outline-none transition-shadow placeholder:text-on-surface-variant/70 focus-visible:ring-2 focus-visible:ring-primary/20"
                />
              </label>
            </div>
            <p className="m-0 rounded-xl bg-primary/5 px-4 py-3 text-xs leading-relaxed text-on-surface-variant">
              <Bilingual {...copy.users.passwordRecovery.securityNotice} />
            </p>
            {errorMessage && (
              <p
                role="alert"
                className="m-0 rounded-xl bg-error/10 px-4 py-3 text-xs font-medium text-error"
              >
                {errorMessage}
              </p>
            )}
          </>
        )}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={state === 'loading'}
            className="h-11 rounded-xl px-4 text-xs font-label font-bold active:scale-95"
          >
            <Bilingual {...(state === 'success' ? copy.common.close : copy.common.cancel)} />
          </Button>
          {state !== 'success' && (
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleSubmit()}
              disabled={state === 'loading'}
              className="h-11 rounded-xl px-5 text-xs font-label font-bold active:scale-95"
            >
              {state === 'loading' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <KeyRound size={16} />
              )}
              <Bilingual
                {...(state === 'loading'
                  ? copy.users.passwordRecovery.savingAction
                  : copy.users.passwordRecovery.saveAction)}
              />
            </Button>
          )}
        </div>
      </div>
    </dialog>
  );
}
